import type { ReactNode } from "react";
import { ChatToolbarTooltip } from "@/components/chat/chat-toolbar-tooltip";
import { cn } from "@/lib/utils";

/**
 * One icon button for a task row's actions (Run now, Pause, Resume, Edit,
 * Delete), shared by the top-bar Tasks view and the agent settings Tasks tab
 * so the two look and behave the same.
 *
 * The rows themselves turn `bg-surface-hover` on hover, so a button using
 * that same colour showed no change under the pointer and looked dead. These
 * use the accent tint (red for Delete) so the button under the pointer always
 * stands out from the hovered row. The label is the app's own tooltip above
 * the button (portalled, so a card or drawer edge cannot clip it), not the
 * browser's small delayed one at the cursor.
 */
export function TaskActionButton({
  label,
  onClick,
  disabled,
  tone = "default",
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  return (
    <ChatToolbarTooltip label={label} portal>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors disabled:opacity-40",
          tone === "danger"
            ? "enabled:hover:bg-red-500/10 enabled:hover:text-red-400"
            : "enabled:hover:bg-accent/10 enabled:hover:text-accent",
        )}
      >
        {children}
      </button>
    </ChatToolbarTooltip>
  );
}
